import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  Checkbox,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Globe20Regular,
  Mail20Regular,
  Chat20Regular,
  CalendarLtr20Regular,
  Document20Regular,
  People20Regular,
} from '@fluentui/react-icons';

// --- Types ---

export type WorkIQArtifactType = 'Email' | 'Teams' | 'Meetings' | 'Documents' | 'People';

export interface WorkIQConfig {
  hubName: string;
  tenantId: string;
  artifactTypes: WorkIQArtifactType[];
}

export interface ConnectWorkIQDialogProps {
  open: boolean;
  onSubmit: (config: WorkIQConfig) => void;
  onCancel: () => void;
}

// --- Styles ---

const useStyles = makeStyles({
  surface: {
    maxWidth: '520px',
    width: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    color: tokens.colorNeutralForeground3,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBlockEnd: tokens.spacingVerticalM,
    lineHeight: '1.5',
  },
  field: {
    marginBlockEnd: tokens.spacingVerticalM,
  },
  input: {
    width: '100%',
  },
  infoBox: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
    marginBlockEnd: tokens.spacingVerticalM,
  },
  artifactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  artifactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  artifactIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  artifactLabel: {
    display: 'flex',
    flexDirection: 'column',
  },
  artifactName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  artifactDesc: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  permNote: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    marginBlockStart: tokens.spacingVerticalXS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

// --- Artifact metadata ---

const artifactMeta: Record<WorkIQArtifactType, { icon: typeof Mail20Regular; description: string }> = {
  Email: { icon: Mail20Regular, description: 'Outlook emails and conversations' },
  Teams: { icon: Chat20Regular, description: 'Teams messages and channel posts' },
  Meetings: { icon: CalendarLtr20Regular, description: 'Meeting transcripts and notes' },
  Documents: { icon: Document20Regular, description: 'OneDrive and SharePoint files' },
  People: { icon: People20Regular, description: 'People directory and org context' },
};

const allArtifactTypes: WorkIQArtifactType[] = ['Email', 'Teams', 'Meetings', 'Documents', 'People'];

// --- Component ---

export const ConnectWorkIQDialog = ({ open, onSubmit, onCancel }: ConnectWorkIQDialogProps) => {
  const styles = useStyles();
  const [hubName, setHubName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [artifactTypes, setArtifactTypes] = useState<Set<WorkIQArtifactType>>(new Set(allArtifactTypes));

  useEffect(() => {
    if (open) {
      setHubName('');
      setTenantId('');
      setArtifactTypes(new Set(allArtifactTypes));
    }
  }, [open]);

  const toggleArtifact = useCallback((type: WorkIQArtifactType) => {
    setArtifactTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const nameValid = hubName.trim().length > 0 && /^[a-zA-Z0-9_-]+$/.test(hubName.trim());
  const canSubmit = nameValid && artifactTypes.size > 0;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({
      hubName: hubName.trim(),
      tenantId: tenantId.trim(),
      artifactTypes: Array.from(artifactTypes),
    });
  }, [canSubmit, hubName, tenantId, artifactTypes, onSubmit]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onCancel(); }}>
      <DialogSurface className={styles.surface}>
        <DialogTitle>
          <div className={styles.titleRow}>
            <Globe20Regular className={styles.titleIcon} />
            <span>Connect to WorkIQ</span>
          </div>
        </DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text className={styles.subtitle}>
              Connect to WorkIQ to give agents access to the caller&apos;s Microsoft 365 artifacts. Retrieval is permission-scoped — agents only see what the user has access to.
            </Text>

            <div className={styles.infoBox}>
              WorkIQ surfaces Email, Teams messages, Meetings, Documents, and People from Microsoft 365. Authentication uses delegated access (On-Behalf-Of) so results are scoped to each user&apos;s permissions.
            </div>

            <Field
              className={styles.field}
              label="Knowledge Hub name"
              hint="A name for this knowledge source in Otto"
              {...(hubName.trim() && !nameValid
                ? { validationMessage: 'Use only letters, numbers, hyphens, and underscores', validationState: 'error' as const }
                : {})}
            >
              <Input
                className={styles.input}
                placeholder="e.g., WorkIQ-MyOrg"
                value={hubName}
                onChange={(_, data) => setHubName(data.value)}
              />
            </Field>

            <Field
              className={styles.field}
              label="Tenant ID"
              hint="Optional — defaults to the current tenant"
            >
              <Input
                className={styles.input}
                placeholder="e.g., 72f988bf-86f1-41af-91ab-2d7cd011db47"
                value={tenantId}
                onChange={(_, data) => setTenantId(data.value)}
              />
            </Field>

            <Field className={styles.field} label="Artifact types" hint="Select which Microsoft 365 sources agents can access">
              <div className={styles.artifactList}>
                {allArtifactTypes.map((type) => {
                  const meta = artifactMeta[type];
                  const Icon = meta.icon;
                  return (
                    <div key={type} className={styles.artifactItem}>
                      <Checkbox
                        checked={artifactTypes.has(type)}
                        onChange={() => toggleArtifact(type)}
                        aria-label={type}
                      />
                      <Icon className={styles.artifactIcon} />
                      <div className={styles.artifactLabel}>
                        <Text className={styles.artifactName}>{type}</Text>
                        <Text className={styles.artifactDesc}>{meta.description}</Text>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Text className={styles.permNote}>
                Results are permission-scoped per user via On-Behalf-Of delegation.
              </Text>
            </Field>
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={onCancel}>Cancel</Button>
            <Button appearance="primary" disabled={!canSubmit} onClick={handleSubmit}>Connect</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
